import { Components, Theme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { IColorOptions } from 'theme/types'
import { colors } from '../colors'
import { fonts } from '../consts'
import { getColorOptions } from '../utils'

export enum ButtonColors {
  Red = 'Red',
  Blue = 'Blue',
  Green = 'Green',
  Gray = 'Gray',
}

export const BUTTONS: Record<ButtonColors, IColorOptions & { hover: string; active: string }> = {
  [ButtonColors.Red]: {
    ...getColorOptions(colors.red),
    hover: colors.redHover,
    active: colors.redActive,
  },
  [ButtonColors.Blue]: {
    ...getColorOptions(colors.blue),
    hover: colors.blueHover,
    active: colors.blueActive,
  },
  [ButtonColors.Green]: {
    ...getColorOptions(colors.green),
    hover: colors.greenHover,
    active: colors.greenActive,
  },
  [ButtonColors.Gray]: {
    ...getColorOptions(colors.gray),
    hover: colors.grayHover,
    active: colors.grayActive,
  },
}
export const buttonColors = Object.keys(ButtonColors) as ButtonColors[]

const overrideButtons = (theme: Theme) =>
  buttonColors.reduce((acc, color) => {
    const buttonColors = BUTTONS[color]

    return {
      ...acc,
      [`&.MuiButton-contained.MuiButton-color${color}`]: {
        background: buttonColors.main,
        color: theme.colors.typographyFive,

        '&:hover': {
          background: buttonColors.hover,
        },
        '&:active': {
          background: buttonColors.active,
        },
        '&:disabled': {
          background: alpha(buttonColors.main, 0.5),
        },
      },
      [`&.MuiButton-contained.MuiButton-colorLight${color}`]: {
        background: buttonColors.secondary,
        color: buttonColors.main,

        '&:hover': {
          background: buttonColors.hover,
          color: theme.colors.typographyFive,
        },
        '&:active': {
          color: theme.colors.typographyFive,
          background: buttonColors.active,
        },
        '&:disabled': {
          background: buttonColors.soft,
          color: alpha(buttonColors.main, 0.5),
        },
      },
      [`&.MuiButton-outlined.MuiButton-color${color}`]: {
        background: theme.backgrounds.backgroundFour,
        border: `1px solid ${theme.backgrounds.backgroundOne}`,
        color: buttonColors.main,
        boxShadow: theme.boxShadows.small,

        '&:hover': {
          background: theme.backgrounds.backgroundFour,
          border: `1px solid ${theme.backgrounds.backgroundSix}`,
        },
        '&:active': {
          background: theme.backgrounds.backgroundFive,
          color: buttonColors.active,
        },
        '&:disabled': {
          background: theme.backgrounds.backgroundFour,
          color: alpha(buttonColors.main, 0.5),
        },
      },
      [`&.MuiButton-text.MuiButton-color${color}`]: {
        color: buttonColors.main,
        background: theme.colors.empty,

        '&:hover': {
          color: buttonColors.main,
          background: buttonColors.soft,
        },
        '&:active': {
          color: buttonColors.active,
          background: buttonColors.secondary,
        },
        '&:disabled': {
          background: theme.colors.empty,
          color: alpha(buttonColors.main, 0.5),
        },
      },
    }
  }, {})

const MuiButton: Components<Theme>['MuiButton'] = {
  defaultProps: {
    variant: 'contained',
    disableRipple: true,
    disableTouchRipple: true,
    disableFocusRipple: true,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      textTransform: 'initial',
      fontFamily: fonts.family.PlusJakartaSans,
      fontWeight: fonts.weight.bold,
      letterSpacing: '0.1px',
      ...overrideButtons(theme),
    }),
    sizeSmall: () => ({
      height: 38,
      borderRadius: 12,
      padding: '10px 18px 8px',
      fontSize: 14,
      gap: 5,
      lineHeight: 1.25,
      'svg, svg *': {
        height: 14,
        width: 14,
      },
    }),
    sizeMedium: () => ({
      height: 42,
      borderRadius: 14,
      padding: '12px 20px 8px',
      fontSize: 14,
      gap: 5,
      lineHeight: 1.25,
      'svg, svg *': {
        height: 14,
        width: 14,
      },
    }),
    sizeLarge: () => ({
      height: 48,
      borderRadius: 14,
      padding: '14px 20px 12px',
      fontSize: 16,
      gap: 5,
      lineHeight: 1.25,
      'svg, svg *': {
        height: 16,
        width: 16,
      },
    }),
  },
}

export const MuiButtonBase: Components<Theme>['MuiButtonBase'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      textTransform: 'initial',
      fontFamily: fonts.family.PlusJakartaSans,
      fontWeight: fonts.weight.bold,
      letterSpacing: '0.1px',
      ...overrideButtons(theme),
    }),
  },
}

export default MuiButton
